import type { MeetingMessageType } from "../components/mail/termineMails";
import { archivierenTrotzZeitlimit, mitSicherungsWarten } from "./mailArchiveRetry";
import { apiUrl } from "../utils/api";
import type { MailArchiveStatusInfo } from "./mailArchiveService";
import { conversationAttachmentSources, dedupeAttachments } from "./mailAttachments";
import { cleanMailPreview } from "./mailPreview";
import { reportMailError } from "./mailError";

// Re-Export für Konsumenten (MailDetailWindow), Logik + Tests in mailAttachments.ts.
export { conversationAttachmentSources, dedupeAttachments };

export type EmailType = "conversation" | "brief" | "normal";

export interface EmailMessage {
    from: string;
    date: string;
    body: string;
}

export interface EmailComment {
    author: string;
    date: string;
    text: string;
}

/**
 * A single mail address (name + address) as delivered by Graph/Backend.
 * Exported as the stable shape for `Email.to`/`Email.cc` — SP2 (Reply-All)
 * prefills the compose window from these arrays.
 */
export interface EmailAddress {
    name: string;
    address: string;
}

/**
 * Erkennung v2 (Mail→Vorgang SP1/SP2): ein Zuweisungs-Kandidat aus der
 * ABSENDER-Adresse — Spiegel des Backend-`SenderCandidate` (mail.dto.ts),
 * FE importiert keinen BE-Typ. Sortierung Firma → AP → Person kommt vom
 * Backend, die Command-Leiste sortiert defensiv nach (mailCommandState.ts).
 */
export interface MailSenderCandidate {
    contactId: number;
    displayName: string;
    kind: "firma" | "ap" | "person";
    /** Bei `kind='ap'`: die Firma, über die abgeleitet wurde. */
    viaCompanyId?: number;
    /** Bei `kind='ap'`: Anzeigename der abgeleiteten Firma (kombinierter Chip). */
    viaCompanyName?: string;
    /** Bei `kind='ap'`: AP-Person hat selbst Status Interessent/Kunde → „anders zuweisen". */
    apHasOwnStatus?: boolean;
    /**
     * Wie der Kandidat gefunden wurde. `'exact'` (Default) = die Adresse steht
     * so im CRM. `'domain'` = nur die Domäne passt zu einer Firma — ein
     * schwächerer Hinweis, der bewusst NICHT vorausgewählt wird. `'betreff'`
     * (T-265) = der Name eines Kunden steht im Betreff; ebenfalls nie
     * vorausgewählt.
     */
    matchedBy?: "exact" | "domain" | "betreff";
    /**
     * In WELCHEM Adressfeld der Mail der Kandidat gefunden wurde (PO 26.07.2026).
     * Spiegel des Backend-Felds (`apps/api/src/mail/sender-candidates.ts`).
     *
     * Das Frontend nutzt es für Reihenfolge und Vorauswahl (`mailCommandState.ts`):
     * nur ein Treffer aus `from` wird vorausgewählt. Optional, weil ältere
     * gecachte Antworten das Feld nicht tragen — fehlt es, gilt `'from'`.
     */
    foundIn?: "from" | "to" | "cc";
    /**
     * Der Kontakt ist ein PRODUKTPARTNER (Versicherer) oder dessen
     * Ansprechpartner — Spiegel des Backend-Felds
     * (`apps/api/src/mail/sender-candidates.ts`), erkannt am Kontaktstatus.
     *
     * Seit T-265 liefert das Backend solche Kandidaten nicht mehr; die Leiste
     * blendet sie trotzdem aus, falls eine ältere gecachte Listen-Annotation
     * sie noch trägt. Optional und nur bei `true` gesetzt.
     */
    istProduktpartner?: boolean;
}

/**
 * Cluster ERKENNUNG — Spiegel der Backend-Typen aus `apps/api/src/mail/mail.dto.ts`.
 * Das Frontend importiert bewusst KEINEN Backend-Typ (Hauskonvention).
 */
export type MailRecognitionStatus =
    | "contact"
    | "company-domain"
    | "company-domain-ambiguous"
    | "internal-only"
    | "none";

export interface MailRecognitionSource {
    messageId: string;
    field: "from" | "to" | "cc";
    address: string;
    displayName: string | null;
    receivedDateTime: string | null;
    /** true = der Treffer stammt aus einer ANDEREN Nachricht der Konversation. */
    fromConversation: boolean;
}

export interface MailRecognizedCompanyDomain {
    domain: string;
    address: string;
    displayName: string | null;
    companies: Array<{ contactId: number; displayName: string }>;
    ambiguous: boolean;
    /** Firmen, die wegen des Deckels nicht in `companies` stehen. */
    more: number;
}

export interface MailContactRecognition {
    status: MailRecognitionStatus;
    contact: { contactId: number; displayName: string; kind: "firma" | "ap" | "person" } | null;
    /**
     * Worauf sich `contact` stützt — feste Rangfolge Betreff → Adresse → Domäne
     * (01.11.2026). `contact` ist DER erkannte Kontakt; alle übrigen Kandidaten
     * sind Auswahl. Optional, weil ein älteres Backend das Feld nicht kennt.
     */
    basis?: "betreff" | "adresse" | "domain" | null;
    /** NUR exakte Treffer — Domänen-Treffer stehen ausschließlich in `companyDomain`. */
    candidates: MailSenderCandidate[];
    /**
     * T-265 — Kunden aus dem BETREFF (`matchedBy: 'betreff'`), ohne
     * Vorauswahl. Optional, weil ein älteres Backend das Feld nicht kennt.
     */
    subjectCandidates?: MailSenderCandidate[];
    source: MailRecognitionSource | null;
    /** Externe Adressen, auf die sich die Erkennung stützt (nie eine interne). */
    displayAddresses: string[];
    companyDomain: MailRecognizedCompanyDomain | null;
    conversationScanned: boolean;
    conversationMessageCount: number;
}

export interface MailSignatureField {
    value: string;
    raw: string;
    line: number;
    confident: boolean;
}

/** T-36: Anschrift aus der Signatur. */
export interface MailSignatureAddress {
    street: string;
    zip: string;
    city: string;
    raw: string;
    line: number;
    confident: boolean;
}

export interface MailParsedSignature {
    found: boolean;
    anchorLine: number | null;
    name: { firstName: string; lastName: string; title: string; raw: string; confident: boolean } | null;
    company: MailSignatureField | null;
    phone: MailSignatureField | null;
    mobile: MailSignatureField | null;
    fax: MailSignatureField | null;
    emails: string[];
    /** T-36 — fehlt bei älteren Antworten. */
    address?: MailSignatureAddress | null;
    /** T-36: Funktion/Position — immer unsicher. */
    position?: MailSignatureField | null;
}

export interface MailMissingContactData {
    contactId: number;
    contactName: string;
    entries: Array<{
        kind: "email" | "phone" | "mobile" | "fax";
        value: string;
        display: string;
        source: "sender" | "signature";
        confident: boolean;
    }>;
}

export interface MailRecognitionResponse {
    contracts: Array<{
        id: number;
        contractNbr: string;
        insuranceNbr: string | null;
        contactId: number | null;
        type: "vertrag";
        riskObjectIds?: number[];
    }>;
    risks: Array<{
        id: number;
        name: string;
        contactId: number | null;
        type: "kfz" | "immobilie";
        plate?: string;
        adresse?: string;
    }>;
    /** Optional: ein Backend ohne Cluster ERKENNUNG liefert das Feld nicht. */
    contactRecognition?: MailContactRecognition | null;
    signature?: MailParsedSignature | null;
    missingContactData?: MailMissingContactData | null;
}

/**
 * Ergebnis von `recognizeMail` — ausdrücklich mit Fehlerzustand.
 *
 * `error` ist NICHT dasselbe wie „nichts erkannt": Im Fehlerfall fällt die
 * Command-Leiste sichtbar auf ihr bisheriges Verhalten zurück, statt stumm zu
 * werden.
 */
export type MailRecognitionOutcome =
    | { status: "ok"; data: MailRecognitionResponse }
    | { status: "error" };

export interface Email {
    id: string;
    conversationId: string;
    /**
     * RFC-Message-Id. Bei ZWEI KOPIEN DERSELBEN Mail identisch — genau daran
     * erkennt die Konversationsansicht eine Selbst-Weiterleitung, die sonst
     * zweimal untereinander stünde (gesendete + empfangene Kopie). `id` taugt
     * dafür nicht: die ist je Kopie verschieden.
     */
    internetMessageId?: string | null;
    senderName: string;
    senderEmail: string;
    senderInitial: string;
    senderColor: string;
    subject: string;
    date: Date;
    dateLabel: string;
    preview: string;
    body: string;
    type: EmailType;
    read: boolean;
    /** Hat die Mail (bzw. IRGENDEINE Nachricht ihrer Konversation) Datei-Anhänge?
     *  (T2 — steuert das Nachladen der Anhang-Liste.) In `groupConversations` über
     *  den ganzen Thread aggregiert. */
    hasAttachments?: boolean;
    /** Message-ids der Konversation, die Anhänge tragen (Repräsentant + Thread).
     *  Behebt die Thread-Lücke: Anhänge hängen oft an einer FRÜHEREN Nachricht. */
    attachmentMessageIds?: string[];
    /** Original To-recipients (from `msg.toRecipients`). Basis für SP2 Reply-All-Prefill. */
    to: EmailAddress[];
    /** Original Cc-recipients (from `msg.ccRecipients`). Basis für SP2 Reply-All-Prefill. */
    cc: EmailAddress[];
    // BEWUSST KEIN `bcc` — Reply-All darf das Original-BCC niemals kennen.
    thread?: EmailMessage[];
    comments?: EmailComment[];
    matchedContact?: { id: number; displayName: string } | null;
    /**
     * SP5 (A3) — ALLE aus from/to/cc aufgelösten, sichtbaren Kontakt-Ids der
     * Mail (dedupliziert). Additiv zu `matchedContact` (Primär). Erlaubt dem
     * Kontakt-Mail-Tab, eine Mail dem geöffneten Kontakt auch als Sekundär-Match
     * (z. B. CC) zuzuordnen. `undefined`/`[]` bei Alt-Cache-Objekten.
     */
    matchedContactIds?: number[];
    /**
     * Cluster ERKENNUNG (A2) — der dem NUTZER angezeigte Kontakt der Mail.
     *
     * Extern-only: ein interner Kollege steht hier NIE, auch wenn er als
     * Kontakt hinterlegt ist. `matchedContact` bleibt davon unberührt — das ist
     * die RBAC-/Relevanzbasis und speist den Schriftverkehr am Kontakt.
     *
     * ROLLE: Listen-Annotation und Notnagel. Die maßgebliche Anzeigequelle im
     * geöffneten Fenster ist `contactRecognition.contact` aus
     * `GET /mail/recognize`; dieses Feld greift, solange die Antwort fehlt oder
     * scheitert (siehe `pickDisplayContact`).
     */
    recognizedContact?: { id: number; displayName: string } | null;
    /** Cluster ERKENNUNG (A2) — alle exakt erkannten, externen Kontakt-Ids. */
    recognizedContactIds?: number[];
    /**
     * Erkennungs-Kandidaten der ABSENDER-Adresse (Mail→Vorgang SP2, Command-
     * Leisten-States). `undefined` = Alt-Cache-Objekt von vor SP1 (Fallback
     * über matchedContact, s. mailCommandState.effectiveSenderCandidates);
     * leeres Array = das Backend hat den Absender wirklich nicht erkannt.
     */
    senderCandidates?: MailSenderCandidate[];
    /**
     * SP4/A2 — grobes Listen-Signal „kommt von intern" (Absenderdomäne).
     * Spoofbar → darf das Auto-Laden externer Inhalte NICHT freischalten.
     * Nur als schwacher Hinweis gedacht; maßgeblich ist `isInternalSenderVerified`.
     */
    isInternalSender?: boolean;
    /**
     * SP4/A2 — VERIFIZIERTES Intern-Flag (interne Domäne UND DMARC=pass), nur aus
     * dem Detail-Load. Erst dieses Flag darf externe Inhalte automatisch laden
     * (Konsistenz-Plan P1-A). Im Listen-Mapping bleibt es undefined.
     */
    isInternalSenderVerified?: boolean;
    /**
     * Mail-SP2 — Graph-Id des Ordners, in dem die Nachricht liegt
     * (`parentFolderId`, additiv aus dem Listen-`select`). In den Suchtreffern
     * wird sie über den geladenen Ordnerbaum je Postfach zu einem Anzeigenamen
     * aufgelöst (Ordner-Chip). `undefined` bei Alt-Cache-Objekten oder
     * Endpoints, die das Feld (noch) nicht liefern.
     */
    parentFolderId?: string;
    /**
     * Mail-SP7 (progressiver Inline-Bilder-Pfad) — NUR aus `fetchMessageBody` im
     * progressive-Modus und nur bei großen/bilderreichen Mails gesetzt: die
     * normalisierten Content-Ids der noch NICHT im Body ersetzten `cid:`-Bilder.
     * `body` trägt dann unersetzte `cid:`-Referenzen; die Komponente lädt je Id
     * `mail/messages/:id/inline/:cid` nach und patcht `cid:`→`data:`
     * (`patchInlineImages`). `undefined`/`[]` = nichts nachzuladen (Default-Pfad
     * bzw. kleine Mail: Body kommt bereits mit `data:`-URIs).
     */
    pendingInlineCids?: string[];
    /**
     * TERMINE (P6) / T-229: `meetingRequest` bei einer Einladung — dann bietet
     * das Lesefenster Zusagen, Mit Vorbehalt und Absagen an. `null`/fehlend bei
     * gewöhnlichen Mails (und bei Listen, die das Feld nicht selektieren).
     */
    meetingMessageType?: MeetingMessageType | null;
}

export interface MailboxAccess {
    id: string;
    mailboxAddress: string;
    displayName: string | null;
    mailAccess: "NONE" | "READ" | "SEND" | "FULL";
    calendarAccess: "NONE" | "READ" | "WRITE";
    canCreateTeamsMeetings: boolean;
    /**
     * Art des Postfachs (Backend: `UserMailboxAccess.mailboxType`). Optional,
     * weil ältere Antworten das Feld nicht tragen — und mit Vorsicht zu lesen:
     * der Default ist `'user'`, geteilte Postfächer sind also nicht zuverlässig
     * als `'shared'` gepflegt. Verlässlich ist nur `'room'` (Raumpostfach).
     */
    mailboxType?: "user" | "room" | "shared";
    /**
     * `true`, wenn dies das EIGENE Postfach des eingeloggten Users ist (Backend:
     * Adresse == Session-E-Mail). Optional, da ältere API-Antworten es nicht
     * tragen — dann greift der E-Mail-Abgleich im MailContext als Fallback.
     */
    isOwn?: boolean;
    /**
     * Jeder Leseberechtigte sieht hier den vollen Ordnerbaum (schaden@) — vom
     * Backend festgelegt. Optional, da ältere API-Antworten es nicht tragen.
     */
    vollerOrdnerbaum?: boolean;
    /**
     * Schadenpostfach (schaden@): an der Mail stehen „Neuer Schaden" und
     * „Einem bestehenden Schaden zuordnen" statt der Vorgangs-Aktionen — vom
     * Backend festgelegt. Optional, da ältere API-Antworten es nicht tragen.
     */
    schadenAblage?: boolean;
    /**
     * Nur in der Kalender-Liste (GET calendar/mailboxes, T-69): Gruppe in der
     * Kalenderauswahl — eigene/Funktionspostfächer, Kollegen, Räume.
     */
    kalenderGruppe?: "eigene" | "kollege" | "raum";
    /**
     * Nur für Kollegenkalender ohne Postfach-Grant (T-69): was die
     * Outlook-Freigabe zeigt — alle Details, Titel und Orte oder nur belegt/frei.
     */
    kalenderStufe?: "details" | "titel" | "belegt";
}

/** Metadaten eines empfangenen Datei-Anhangs (T2) — Spiegel des Backend-`MailAttachmentMeta`. */
export interface MailAttachment {
    id: string;
    name: string;
    contentType: string;
    size: number;
    isInline: boolean;
    contentId?: string | null;
    /** Nachricht, an der dieser Anhang hängt. In einer Konversation kann das eine
     *  FRÜHERE Thread-Nachricht sein (nicht der angezeigte Repräsentant) — der
     *  Download braucht diese id. Von `fetchAttachments` gestempelt. */
    messageId: string;
}

/** Ein zu übernehmender Anhang (Wire-Shape zum Backend-`ImportMailAttachmentItemDto`). */
export interface MailAttachmentImportItem {
    messageId: string;
    attachmentId: string;
    /** Optionaler Wunsch-Basename (ohne Extension). Ohne Angabe übernimmt das
     *  Backend den Graph-Originalnamen; die Extension wird serverseitig erzwungen. */
    name?: string;
}

/** Ergebnis eines einzelnen Anhang-Imports (Spiegel Backend). */
export interface MailAttachmentImportResult {
    messageId: string;
    attachmentId: string;
    documentId: number | null;
    /** `linked` (T-18): Datei lag schon in der Akte, im Vorgang verknüpft statt kopiert. */
    status: "created" | "duplicate" | "linked" | "error";
    error?: string;
}

/**
 * Antwort von `POST /mail/send` (PO 26.07.2026).
 *
 * Der Versand selbst ist mit `ok` erledigt; die beiden anderen Felder betreffen
 * die automatische Vorgangs-Verknüpfung. Sie ist bewusst NICHT Teil des
 * Sendeergebnisses — die Mail ist raus, auch wenn die Verknüpfung scheitert.
 * Früher wurde ein solcher Fehler nur ins Server-Log geschrieben und niemand
 * merkte, dass die Mail nicht am Vorgang hängt.
 */
/** T-212 — Datei-Anhang, der schon im Entwurf liegt. */
export interface EntwurfAnhang {
    id: string;
    name: string;
    contentType: string;
    size: number;
    isInline: boolean;
}

export type EntwurfModus = "new" | "reply" | "replyAll" | "forward";

/** T-212 — KMVS-Kontext am Entwurf (Vorgang, Antwortbezug). */
export interface EntwurfKontext {
    mode: EntwurfModus;
    replyToMessageId?: string | null;
    replySourceMailbox?: string | null;
    vorgangId?: number | null;
    /** T-259: To-do der Mail („Mail schreiben" an einer To-do-Karte). */
    todoId?: number | null;
    inheritLinksFromMessageId?: string | null;
    contactId?: number | null;
}

/** T-212 — Stand eines Entwurfs nach dem Speichern. */
export interface EntwurfStand {
    id: string;
    mailbox: string;
    lastModifiedDateTime: string | null;
    conversationId: string | null;
    attachments: EntwurfAnhang[];
}

/** T-212 — ein Entwurf zum Weiterschreiben. */
export interface EntwurfDetail extends EntwurfStand {
    subject: string;
    to: string[];
    cc: string[];
    bcc: string[];
    bodyHtml: string;
    kontext: EntwurfKontext | null;
}

/** T-231 — ein aus einem Vorgang begonnener Entwurf. */
export interface VorgangEntwurf {
    mailbox: string;
    draftId: string;
    subject: string;
    to: string[];
    lastModifiedDateTime: string | null;
    /** An diesem To-do steht der Entwurf; `null`/fehlt = am Vorgang selbst. */
    todoId?: number | null;
    /** Anriss des Textes — die Verlaufskarte zeigt ihn wie bei einer Mail. */
    preview?: string;
}

/** T-212 (Teil B) — `conversationId → jüngster Antwort-Entwurf`. */
export type EntwurfIndex = Record<string, { draftId: string; lastModifiedDateTime: string | null }>;

/** T-212 — Inhalt beim Speichern/Senden eines Entwurfs. */
export interface EntwurfPayload {
    mailbox: string;
    to: string[];
    cc: string[];
    bcc: string[];
    subject: string;
    body: string;
    mode: EntwurfModus;
    replyToMessageId?: string;
    replySourceMailbox?: string;
    vorgangId?: number;
    todoId?: number;
    inheritLinksFromMessageId?: string;
    contactId?: number;
    /** NEUE Anhänge (Inline-Bilder immer). */
    attachments?: SendMailAttachment[];
    /** Datei-Anhänge des Entwurfs, die bleiben sollen. */
    behalteAnhangIds?: string[];
    /** `lastModifiedDateTime` des zuletzt gesehenen Stands (Konfliktschutz). */
    bekannteAenderung?: string | null;
}

export interface SendMailResult {
    ok: true;
    /** Vorgänge, an die die gesendete Mail tatsächlich gehängt wurde. */
    linkedVorgangIds?: number[];
    /** Meldung für den Absender, wenn die Verknüpfung nicht zustande kam. */
    linkWarning?: string;
    /** T-340: nur bei Versand mit Kennung. */
    status?: "gesendet";
    sendeId?: string;
    /**
     * T-340: Der Lauf war dem Server nicht mehr bekannt (z. B. nach einem
     * Neustart), die Mail lag aber in „Gesendete Elemente“.
     */
    nachgeschlagen?: boolean;
}

/**
 * T-340: Antwort auf einen Versand mit Kennung bzw. auf die Statusabfrage.
 * `laeuft`: Der Server arbeitet noch — den Ausgang über `holeVersandStatus`
 * erfragen, NICHT erneut senden.
 */
export type VersandAntwort =
    | SendMailResult
    | { status: "laeuft"; sendeId: string }
    | { status: "fehlgeschlagen"; sendeId: string; fehler: { status: number; message: string } }
    | { status: "unbekannt"; sendeId: string };

/** T-340: Kennung + Wiederholungs-Hinweis für einen Versand. */
export interface VersandKennung {
    /** Eine Kennung je Verfassen-Fenster — gleiche Kennung sendet nie zweimal. */
    sendeId?: string;
    /** Erneuter Versuch nach offenem oder gescheitertem Ausgang. */
    wiederholung?: boolean;
}

/** Gesamtergebnis von `POST /mail/attachments/import`. */
export interface MailAttachmentImportSummary {
    imported: number;
    skipped: number;
    results: MailAttachmentImportResult[];
}

/**
 * Ein Treffer der Empfänger-Kontaktsuche (SP5/C2). Spiegelt das Backend-Shape
 * `EmailRecipientHit` (contact.dto.ts) — FE importiert keinen BE-Typ.
 */
export interface EmailRecipientHit {
    displayName: string;
    email: string;
    kind: "privat" | "geschaeftlich";
    isExtern: boolean;
    contactId: number;
    companyName?: string;
    /**
     * T-22: Vorschlag zum Kunden des Vorgangs (Kontextgruppe) — steht vorn
     * und bekommt im Dropdown eine eigene Überschrift.
     */
    kontext?: boolean;
}

/**
 * SP6 (C3) — Wire-Shape für Mail-Anhänge (1:1 zum Backend `SendMailAttachmentDto`).
 * Inline-Bilder: `isInline: true` + `contentId` (matcht `<img src="cid:<id>">`).
 */
export interface SendMailAttachment {
    name: string;
    /** z.B. "image/png"; image/* für inline, sonst dokument-Whitelist. */
    contentType: string;
    /**
     * Base64 OHNE "data:...;base64,"-Präfix. Entfällt bei `documentId` — dann
     * holt der Server den Inhalt selbst.
     */
    contentBytes?: string;
    /**
     * KMVS-Dokument statt hochgeladener Datei (PO 26.07.2026, Punkt 5). Es wird
     * nur die Id gesendet; Name, MIME-Typ und Bytes kommen server-seitig aus der
     * Datenbank. Das spart den doppelten Netzweg und umgeht das Request-Limit.
     */
    documentId?: number;
    /**
     * Anhang aus einer BESTEHENDEN Nachricht übernehmen (Weiterleiten). Wie bei
     * `documentId` reist nur der Zeiger — die Bytes holt der Server aus der
     * Ursprungsnachricht, nachdem er den Lesezugriff darauf geprüft hat.
     * Gelesen wird im Postfach, das der Versand als `replySourceMailbox` nennt.
     */
    sourceMessageId?: string;
    sourceAttachmentId?: string;
    /**
     * Eine Mail aus dem Vorgang als Anhang (`.eml`, User 22.10.2026) — die Id
     * ihrer Verknüpfung. Wie bei `documentId` reist nur der Zeiger; der Server
     * lädt die Mail und prüft dabei, ob der Absender sie lesen darf.
     */
    mailLinkId?: number;
    isInline?: boolean;
    contentId?: string;
}

// Exportiert, damit auch Mails, die NICHT aus dem Graph-Listing stammen
// (L1: die Snapshots verknuepfter Vorgangs-Mails), dieselbe Avatar-Farbe und
// dasselbe Datumsformat bekommen wie die Ordnerliste.
export const stringToColor = (str: string) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const c = (hash & 0x00FFFFFF).toString(16).toUpperCase();
    return "#" + "00000".substring(0, 6 - c.length) + c;
};

export const getDateLabel = (date: Date): string => {
    const today = new Date();
    const isToday = date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
    const isYesterday = new Date(today.getTime() - 86400000).getDate() === date.getDate();

    if (isToday) {
        return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', hour12: false });
    }
    if (isYesterday) {
        return "Gestern";
    }
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
};

type BackendMailListItem = {
    id: string;
    conversationId: string;
    internetMessageId?: string | null;
    subject: string;
    from: { name: string; address: string } | null;
    toRecipients: { name: string; address: string }[];
    ccRecipients: { name: string; address: string }[];
    receivedDateTime: string;
    bodyPreview: string;
    hasAttachments: boolean;
    isRead: boolean;
    matchedContact?: { id: number; displayName: string } | null;
    matchedContactIds?: number[];
    /** Cluster ERKENNUNG (A2) — Anzeige-Erkennung, extern-only. */
    recognizedContact?: { id: number; displayName: string } | null;
    recognizedContactIds?: number[];
    senderCandidates?: MailSenderCandidate[];
    isInternalSender?: boolean;
    parentFolderId?: string;
    /** TERMINE (P6): Einladung/Zusage/Absage; `null` bei gewöhnlicher Mail. */
    meetingMessageType?: MeetingMessageType | null;
};

export type BackendMailDetail = BackendMailListItem & {
    body: { contentType: string; content: string } | null;
    isInternalSenderVerified?: boolean;
    /** Mail-SP7 — Content-Ids der progressiv nachzuladenden Inline-Bilder. */
    pendingInlineCids?: string[];
};

function escapeHtml(text: string): string {
    return text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
}

// Graph mail bodies can be HTML or plain text. SandboxedMailBody renders HTML,
// so a plain-text body must be escaped and its newlines turned into <br> —
// otherwise line breaks collapse and the body looks like one run-on block.
function renderBody(body: { contentType: string; content: string }): string {
    const content = body.content ?? "";
    if ((body.contentType ?? "").toLowerCase() === "html") return content;
    return escapeHtml(content).replace(/\r?\n/g, "<br>");
}

export const mapItem = (msg: BackendMailListItem, detail?: BackendMailDetail): Email => {
    const senderName = msg.from?.name || msg.from?.address || "Unbekannt";
    const senderEmail = msg.from?.address || "";
    const senderInitial = senderName.charAt(0).toUpperCase() || "?";
    const date = new Date(msg.receivedDateTime);

    return {
        id: msg.id,
        conversationId: msg.conversationId || msg.id,
        internetMessageId: msg.internetMessageId ?? null,
        senderName,
        senderEmail,
        senderInitial,
        senderColor: stringToColor(senderName),
        subject: msg.subject || "Kein Betreff",
        date,
        dateLabel: getDateLabel(date),
        // SP3: Quoted-Reply-Artefakte („____", „Von: …") aus dem Graph-
        // bodyPreview schneiden — der Snippet zeigt nur den Antworttext.
        preview: cleanMailPreview(msg.bodyPreview || ""),
        body: detail?.body ? renderBody(detail.body) : "",
        type: "normal",
        read: msg.isRead,
        hasAttachments: msg.hasAttachments ?? false,
        // Defensive `?? []`: ältere, aus dem SWR-localStorage rehydrierte Objekte
        // (vor diesem PR gecacht) tragen `toRecipients`/`ccRecipients` evtl. nicht.
        to: msg.toRecipients ?? [],
        cc: msg.ccRecipients ?? [],
        matchedContact: msg.matchedContact ?? null,
        // SP5 (A3): alle sichtbaren from/to/cc-Matches; `?? []` deckt Alt-Cache
        // und Endpoints ohne Annotation ab (Filter fällt dann auf Adress-Match
        // bzw. den Primär-`matchedContact` zurück).
        matchedContactIds: msg.matchedContactIds ?? [],
        // Cluster ERKENNUNG (A2): die Anzeige-Erkennung durchreichen. Fehlt sie
        // (älteres Backend, Alt-Cache), bleibt `null`/`[]` — das Fenster fällt
        // dann auf den Legacy-Pfad zurück.
        recognizedContact: msg.recognizedContact ?? null,
        recognizedContactIds: msg.recognizedContactIds ?? [],
        // BEWUSST ohne `?? []`: undefined heißt „Endpoint/Alt-Stand ohne
        // Annotation" und triggert den matchedContact-Fallback der Command-
        // Leiste; ein leeres Array ist die echte Backend-Aussage „nicht erkannt".
        senderCandidates: msg.senderCandidates,
        isInternalSender: msg.isInternalSender ?? false,
        // Nur das DETAIL trägt das verifizierte Flag (DMARC=pass). Beim reinen
        // Listen-Mapping bleibt es undefined → Auto-Load erst nach Detail-Load.
        isInternalSenderVerified: detail?.isInternalSenderVerified ?? undefined,
        // Mail-SP2: Ordner-Id durchreichen (Detail hätte Vorrang, trägt aber
        // dasselbe Feld) → Ordner-Chip in den Suchtreffern.
        parentFolderId: detail?.parentFolderId ?? msg.parentFolderId,
        // Mail-SP7: nur das progressive DETAIL trägt die nachzuladenden cid-Ids.
        pendingInlineCids: detail?.pendingInlineCids,
        // T-229: Ist es eine Einladung, bietet das Lesefenster Zusagen/Absagen an.
        meetingMessageType: detail?.meetingMessageType ?? msg.meetingMessageType ?? null,
    };
};

/** T-229 — die Einladung hinter einer Termin-Mail (`GET mail/messages/:id/einladung`). */
export type EinladungsAntwort = "zusagen" | "vorbehalt" | "absagen";
export type TeilnahmeStatus =
    | "none"
    | "organizer"
    | "tentativelyAccepted"
    | "accepted"
    | "declined"
    | "notResponded";
export interface TerminEinladung {
    eventId: string;
    betreff: string;
    start: { dateTime: string; timeZone: string } | null;
    ende: { dateTime: string; timeZone: string } | null;
    ganztaegig: boolean;
    ort: string | null;
    organisator: { name: string | null; adresse: string | null } | null;
    status: TeilnahmeStatus;
    abgesagt: boolean;
    istOrganisator: boolean;
    /** T-271: Erwartet der Organisator eine Antwort? Fehlt bei älteren Antworten des Servers. */
    antwortErwuenscht?: boolean;
}

/**
 * The SWR cache persists to localStorage, so after a reload `date` comes back
 * as an ISO string (JSON has no Date type). Re-hydrate it so the
 * `Email.date: Date` contract holds for every consumer.
 */
function reviveEmailDates(emails: Email[]): Email[] {
    return emails.map((e) =>
        e.date instanceof Date ? e : { ...e, date: new Date(e.date as unknown as string) },
    );
}

/**
 * B5 — kein hängender Spinner mehr: jeder Call bekommt ein hartes Timeout
 * (AbortController; `AbortSignal.timeout` fehlt in unserer TS-DOM-Lib), und
 * idempotente GETs bekommen bei Netz-/Timeout-Fehlern genau EINEN Retry.
 * Mutationen (POST/PATCH) werden bewusst NIE automatisch wiederholt.
 */
export const FETCH_TIMEOUT_MS = 15_000;

/**
 * T-45: Zeitlimit NUR für den Versand (`POST /mail/send`). Der Server erledigt
 * dort in einem Request den Graph-Versand bzw. die Graph-Antwort, die
 * Vorgangs-Verknüpfung und die Mitteilung — das dauerte auf PROD knapp über
 * 15 s. Mit dem allgemeinen Limit brach das Frontend ab und meldete
 * „fehlgeschlagen", obwohl die Mail raus war (Gefahr: doppelter Versand).
 * Alle anderen Abrufe bleiben bewusst bei `FETCH_TIMEOUT_MS`.
 *
 * 70 s und nicht mehr: Auf PROD kappt nginx `location /api/` nach
 * `proxy_read_timeout 75s` mit einem 504. Ein längeres Client-Limit griffe
 * nie — der Abbruch kommt dann als 504 vom Proxy. Knapp darunter bleibt die
 * Zeitüberschreitung in unserer Hand (502/504 beim Versand werden in
 * `mailError.ts` trotzdem wie eine Zeitüberschreitung gemeldet).
 */
export const SEND_TIMEOUT_MS = 70_000;

/**
 * Zeitlimit fürs Archivieren. Der Server sichert eine verknüpfte Mail vor dem
 * Verschieben (bis zu 6 s) und spricht davor und danach mehrmals mit Graph —
 * bei langsamem Graph reichten 15 s nicht (PROD 26.10.2026). Knapp unter den
 * 30 s, nach denen der Proxy auf DEV kappt.
 */
export const ARCHIVE_TIMEOUT_MS = 28_000;

const fetchWithTimeout = async (
    input: string,
    init?: RequestInit,
    timeoutMs: number = FETCH_TIMEOUT_MS,
): Promise<Response> => {
    const ctrl = new AbortController();
    const timer = window.setTimeout(() => ctrl.abort(), timeoutMs);
    try {
        return await fetch(input, { ...init, signal: ctrl.signal });
    } finally {
        window.clearTimeout(timer);
    }
};

const fetchJson = async <T>(
    input: string,
    init?: RequestInit,
    opts: { timeoutMs?: number } = {},
): Promise<T> => {
    const isGet = !init?.method || init.method.toUpperCase() === "GET";
    let res: Response;
    try {
        res = await fetchWithTimeout(input, init, opts.timeoutMs);
    } catch (err) {
        if (!isGet) throw err;
        // Netz-/Timeout-Fehler auf GET → ein zweiter Versuch.
        res = await fetchWithTimeout(input, init, opts.timeoutMs);
    }
    if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`${res.status} ${res.statusText}: ${text}`);
    }
    return res.json();
};

/**
 * Erkennt einen echten Graph-„Not Found" (HTTP 404) an der von `fetchJson`
 * erzeugten Fehlermeldung (`"404 Not Found: …"`). Dient dazu, eine definitive
 * „nicht vorhanden"-Meldung von transienten Fehlern (Timeout/5xx) zu trennen
 * (A4: „Nachricht konnte nicht geladen werden" nur bei echtem 404).
 */
export function isGraph404(err: unknown): boolean {
    const msg = err instanceof Error ? err.message : typeof err === "string" ? err : "";
    return /^\s*404\b/.test(msg);
}

/**
 * „Mail für diesen Nutzer nicht verfügbar" — dauerhaft, nicht transient: 403
 * (kein Zugriff über Postfach/Vorgang/Freigabe) ODER 404 (Nachricht existiert
 * nicht mehr). Dient der graziösen Anzeige „nicht verfügbar" statt eines
 * „Neu laden"-Retrys (der hier nichts bringt).
 */
export function isMailUnavailable(err: unknown): boolean {
    const msg = err instanceof Error ? err.message : typeof err === "string" ? err : "";
    return /^\s*(403|404)\b/.test(msg);
}

export function groupConversations(emails: Email[]): Email[] {
    const threadGroups: Record<string, Email[]> = {};
    for (const em of emails) {
        if (!threadGroups[em.conversationId]) {
            threadGroups[em.conversationId] = [];
        }
        threadGroups[em.conversationId].push(em);
    }

    const result: Email[] = [];

    for (const cId in threadGroups) {
        const group = threadGroups[cId];
        group.sort((a, b) => b.date.getTime() - a.date.getTime());

        const mainEmail = group[0];

        // Anhänge über den GANZEN Thread aggregieren — sonst zeigt eine Konversation
        // keinen Anhang, wenn er an einer früheren (nicht der neuesten) Nachricht hängt.
        const attSrc = conversationAttachmentSources(group);
        mainEmail.hasAttachments = attSrc.hasAttachments;
        mainEmail.attachmentMessageIds = attSrc.attachmentMessageIds;

        if (group.length > 1) {
            mainEmail.type = "conversation";
            mainEmail.thread = group.slice(1).map(e => ({
                from: e.senderName,
                date: e.date.toLocaleString('de-DE'),
                body: e.body,
            }));
        }
        result.push(mainEmail);
    }

    result.sort((a, b) => b.date.getTime() - a.date.getTime());
    return result;
}

/**
 * T-29 — jede Nachricht als eigene Zeile, neueste zuerst.
 *
 * Erzeugt NEUE Objekte: die Eingaben stammen oft aus einem Cache oder aus
 * `unreadRawRef` und werden anderswo noch gruppiert — `groupConversations`
 * verändert das Haupt-Element, hier darf nichts davon zurückbleiben. Die
 * Anhang-Markierung gilt wieder pro Nachricht, nicht über den Verlauf.
 */
export function einzelneMails(emails: readonly Email[]): Email[] {
    return emails
        .map((e) => ({
            ...e,
            type: "normal" as Email["type"],
            thread: undefined,
            attachmentMessageIds: e.hasAttachments ? [e.id] : [],
        }))
        .sort((a, b) => b.date.getTime() - a.date.getTime());
}

/**
 * T-29 — Optionen der Ordner-Abrufe. `gruppiert: false` liefert jede Nachricht
 * als eigene Zeile (Einzelansicht der Mail-App); Standard bleibt die
 * Zusammenfassung zu Unterhaltungen, damit andere Aufrufer (Kundenakte,
 * OutlookEmails) unverändert bleiben.
 */
export interface ListenOptionen {
    gruppiert?: boolean;
}

/**
 * T-29 — gecacht wird die UNGRUPPIERTE Liste, zusammengefasst erst hier. So
 * bedienen dieselben Cache-Einträge beide Ansichten. Gruppiert wird auf
 * KOPIEN: `groupConversations` verändert das Haupt-Element, und ein im Cache
 * mutiertes Objekt vererbte sein `thread` an die jeweils andere Ansicht.
 * (Ein noch vor T-29 geschriebener, bereits gruppierter Eintrag wird dadurch
 * ebenfalls sauber neu gefaltet; er läuft nach 20 s ohnehin ab.)
 */
function alsListe(roh: Email[], opts?: ListenOptionen): Email[] {
    const liste = reviveEmailDates(roh);
    if (opts?.gruppiert === false) return einzelneMails(liste);
    return groupConversations(
        liste.map((e) => ({ ...e, type: "normal" as EmailType, thread: undefined })),
    );
}

const ensureMailbox = (mailbox: string | null | undefined): string => {
    if (!mailbox) throw new Error("Kein Postfach ausgewählt");
    return mailbox;
};

/**
 * T-179 — Ordner-Parameter der Seitenabrufe. Der Posteingang reist OHNE
 * `folder` (die Anfrage bleibt die alte, auch der Cache-Schlüssel), jeder andere
 * Ordner mit seinem Well-Known-Namen bzw. seiner unveränderten Graph-Id.
 */
const seitenOrdner = (folder: string | null | undefined): string | null =>
    !folder || folder === "inbox" ? null : folder;

import { cached, invalidate, invalidateByPrefix, poke } from "../utils/swrCache";
import { meldeEntwurfGeaendert } from "../components/mail/entwurfBus";

/**
 * Mail-SP7 — Kill-Switch für den progressiven Inline-Bilder-Pfad. Default AN;
 * per `VITE_MAIL_PROGRESSIVE_INLINE="false"` oder
 * `localStorage.kmvs.mailProgressiveInline === "0"` sofort deaktivierbar (dann
 * lädt `fetchMessageBody` wie bisher den ganzen Body inkl. Inline-Bilder in EINEM
 * Call). Der Server gatet zusätzlich per Heuristik (kleine Mails bleiben synchron).
 */
export function isMailProgressiveInlineEnabled(): boolean {
    try {
        if (import.meta.env?.VITE_MAIL_PROGRESSIVE_INLINE === "false") return false;
        return !(typeof localStorage !== "undefined" && localStorage.getItem("kmvs.mailProgressiveInline") === "0");
    } catch {
        return true;
    }
}

/**
 * Mail-SP7 — ersetzt im Mail-HTML `src="cid:<id>"` durch die geladene `data:`-URI
 * (Spiegel des Backend-`rewriteInlineCidToDataUri`). `byCid` ist auf die
 * normalisierte, klein geschriebene Content-Id geschlüsselt; unbekannte `cid:`
 * bleiben unangetastet (Bild bleibt leer — graceful).
 */
export function replaceCidWithDataUri(html: string, byCid: Map<string, string>): string {
    if (!html || byCid.size === 0) return html;
    const uriFuer = (rawCid: string): string | undefined => byCid.get(cidSchluessel(rawCid));
    // `src=`/`background=` und CSS-`url(cid:…)` — dieselben drei Formen, die der
    // Server auflöst (`graph-mail.service`), sonst blieben nachgeladene
    // Hintergrundbilder leer.
    return html
        .replace(
            /(\b(?:src|background)\s*=\s*)(['"]?)cid:([^'">\s]+)\2/gi,
            (match, prefix: string, quote: string, rawCid: string) => {
                const dataUri = uriFuer(rawCid);
                if (!dataUri) return match;
                const q = quote || '"';
                // T-236: Marker nur an `src=` (Bild) — Spiegel von `cidMarkerAttribut`.
                const marker = /^src/i.test(prefix) ? cidMarkerAttribut(rawCid) : "";
                return `${marker}${prefix}${q}${dataUri}${q}`;
            },
        )
        .replace(
            /(url\(\s*)(['"]?)cid:([^'")\s]+)\2(\s*\))/gi,
            (match, prefix: string, quote: string, rawCid: string, ende: string) => {
                const dataUri = uriFuer(rawCid);
                if (!dataUri) return match;
                return `${prefix}${quote}${dataUri}${quote}${ende}`;
            },
        );
}

/**
 * T-236 — Marker am `<img>`, dessen `cid:` ersetzt wird: darüber findet das
 * Lese-Fenster vom Bild im Mailtext zum Anhang. Die Content-Id kommt aus der
 * Mail; außerhalb des engen Zeichenvorrats gibt es keinen Marker.
 */
export function cidMarkerAttribut(rawCid: string): string {
    const key = cidSchluessel(rawCid);
    return /^[a-z0-9._@+$-]+$/.test(key) ? `data-kmvs-cid="${key}" ` : "";
}

/**
 * Schlüssel einer `cid:`-Referenz: ohne `< >`, klein, und PROZENT-DEKODIERT —
 * RFC 2392 kodiert die Content-Id im Body als URL (`bild%40firma.de`), der
 * Server meldet sie dekodiert (`bild@firma.de`). Spiegel von `normalizeCid`.
 */
export function cidSchluessel(rawCid: string): string {
    const nackt = rawCid.trim().replace(/^<|>$/g, "");
    let dekodiert = nackt;
    if (nackt.includes("%")) {
        try {
            dekodiert = decodeURIComponent(nackt);
        } catch {
            dekodiert = nackt;
        }
    }
    return dekodiert.toLowerCase();
}

const MAIL_TTL_MS = 20_000; // 20s — mail is most-frequently refreshed list
const FOLDER_TTL_MS = 5 * 60_000; // Ordnerbaum ändert sich selten → längeres TTL

/**
 * Ein Mail-Ordner (T3), als verschachtelter Baum — Spiegel des Backend-`MailFolder`
 * (GET /mail/folders). `unreadItemCount`/`totalItemCount` sind die Graph-Zähler.
 */
export interface MailFolder {
    id: string;
    displayName: string;
    parentFolderId: string | null;
    childFolderCount: number;
    unreadItemCount: number;
    totalItemCount: number;
    children: MailFolder[];
    /** Vom Backend für den Posteingang gesetzt ('inbox'). */
    wellKnownName?: string | null;
}

/** Meta der paginierten Posteingangs-Liste — Spiegel des Backend-`InboxPagedResponse`. */
export interface UnreadMeta {
    /** Sichtbare Items dieser Antwort (nach per-Kontakt-RBAC). */
    fetched: number;
    /** Durch Berechtigungen ausgeblendete Items dieser Antwort (nur Zähler). */
    hiddenByRbac: number;
    /** Weitere Items vorhanden → Folgeseite mit `nextSkip`. */
    hasMore: boolean;
    nextSkip: number;
}

/** Eine Seite der Ungelesen-Liste: gemappte, UNGRUPPIERTE Emails + Meta. */
export interface UnreadPageResult {
    emails: Email[];
    meta: UnreadMeta;
}

/** T-212 — Wire-Form eines Entwurfs; leere Optionalfelder fallen weg. */
function entwurfBody(p: EntwurfPayload): Record<string, unknown> {
    return {
        mailbox: p.mailbox,
        to: p.to,
        cc: p.cc,
        bcc: p.bcc,
        subject: p.subject,
        body: p.body,
        mode: p.mode,
        replyToMessageId: p.replyToMessageId || undefined,
        replySourceMailbox: p.replySourceMailbox ? ensureMailbox(p.replySourceMailbox) : undefined,
        vorgangId: p.vorgangId ?? undefined,
        todoId: p.todoId ?? undefined,
        inheritLinksFromMessageId: p.inheritLinksFromMessageId || undefined,
        contactId: p.contactId ?? undefined,
        attachments: p.attachments?.length ? p.attachments : undefined,
        behalteAnhangIds: p.behalteAnhangIds?.length ? p.behalteAnhangIds : undefined,
        bekannteAenderung: p.bekannteAenderung || undefined,
    };
}

export const graphMailService = {
    async listMailboxes(): Promise<MailboxAccess[]> {
        // B11: kurze TTL (statt 5 min) — der Cache ist localStorage-persistiert;
        // mit langer TTL blieben frisch verteilte Freigaben selbst nach Reload
        // minutenlang unsichtbar. 60 s reicht als Lastschutz völlig.
        return cached(
            "mail.mailboxes",
            () => fetchJson<MailboxAccess[]>(apiUrl("mail/mailboxes")),
            { ttlMs: 60_000 },
        );
    },

    /**
     * NACHTRAG 2026-07-05 — Posteingang (Erstseite, gecacht): 1:1 der Graph-
     * Inbox-Ordner (gelesene UND ungelesene, keine Unterordner) mit Paginierung.
     * Liefert UNGRUPPIERTE Emails + Meta; der Aufrufer gruppiert über die
     * akkumulierte Liste (`groupConversations`), damit „Mehr laden" Threads merged.
     *
     * T-179 (28.10.2026): `folder` liefert dasselbe für jeden anderen Ordner
     * (Gesendet, Archiv, Unterordner …) — vorher endeten diese Listen fest nach
     * 100 Mails. Jeder Ordner hat seinen eigenen Cache-Eintrag.
     */
    async fetchInboxPaged(mailbox: string, folder?: string | null): Promise<UnreadPageResult> {
        const ordner = seitenOrdner(folder);
        const res = await cached(
            ordner ? `mail.inbox.paged:${mailbox}:${ordner}` : `mail.inbox.paged:${mailbox}`,
            async () => {
                const r = await fetchJson<{ items: BackendMailListItem[]; meta: UnreadMeta }>(
                    apiUrl("mail/inbox-paged", {
                        mailbox: ensureMailbox(mailbox),
                        top: 100,
                        ...(ordner ? { folder: ordner } : {}),
                        // Einladungen erkennen (Termin-Zeile wie Outlook, #2397).
                        // Lehnt Graph das ab, liefert der Server die Seite ohne.
                        mitTerminArt: true,
                    }),
                );
                return { emails: r.items.map((m) => mapItem(m)), meta: r.meta };
            },
            { ttlMs: MAIL_TTL_MS },
        );
        return { emails: reviveEmailDates(res.emails), meta: res.meta };
    },

    /**
     * Folgeseite („Mehr laden", `skip` = `meta.nextSkip`) des Posteingangs oder
     * — T-179 — eines anderen Ordners (`folder`). Bewusst ungecacht.
     */
    async fetchInboxPage(mailbox: string, skip: number, folder?: string | null): Promise<UnreadPageResult> {
        const ordner = seitenOrdner(folder);
        const r = await fetchJson<{ items: BackendMailListItem[]; meta: UnreadMeta }>(
            apiUrl("mail/inbox-paged", {
                mailbox: ensureMailbox(mailbox),
                top: 100,
                skip,
                ...(ordner ? { folder: ordner } : {}),
                mitTerminArt: true,
            }),
        );
        return { emails: r.items.map((m) => mapItem(m)), meta: r.meta };
    },

    /**
     * Thread-Index des Postfachs: `conversationId` → Anzahl der eigenen
     * Antworten in „Gesendet". Damit setzt die Liste den Aufklapp-Pfeil, ohne
     * je Zeile einen eigenen Abruf zu brauchen.
     *
     * ANREICHERUNG: Der Aufrufer lädt das nebenläufig und rendert vorher. Ein
     * Fehler ist kein Fehler der Liste — er liefert eine leere Karte, und die
     * Zeilen verhalten sich wie vor dieser Änderung.
     */
    async fetchThreadCounts(mailbox: string): Promise<Record<string, string[]>> {
        try {
            return await cached(
                `mail.threadcounts:${mailbox}`,
                () =>
                    fetchJson<Record<string, string[]>>(
                        apiUrl("mail/thread-counts", { mailbox: ensureMailbox(mailbox) }),
                    ),
                { ttlMs: MAIL_TTL_MS },
            );
        } catch {
            return {};
        }
    },

    /**
     * SP4 — alle Nachrichten einer Konversation (Outlook-Abschnitte), älteste
     * zuerst. Kurze TTL: die Ansicht soll beim erneuten Öffnen frisch sein,
     * Realtime-Frames invalidieren ohnehin postfachweit.
     */
    async fetchConversation(mailbox: string, conversationId: string): Promise<Email[]> {
        return reviveEmailDates(await cached(
            `mail.conv:${mailbox}:${conversationId}`,
            async () => {
                const items = await fetchJson<BackendMailListItem[]>(
                    apiUrl("mail/conversation", { mailbox: ensureMailbox(mailbox), conversationId }),
                );
                return items.map((m) => mapItem(m));
            },
            { ttlMs: 30_000 },
        ));
    },

    /**
     * SP2 — postfachweite Suche fürs Suchfeld (freier Text oder from:/to:/subject:).
     * Bewusst UNgecacht (ad-hoc) und UNgruppiert (Suchtreffer = einzelne Mails).
     */
    async searchMail(mailbox: string, q: string, top = 50): Promise<Email[]> {
        const items = await fetchJson<BackendMailListItem[]>(
            apiUrl("mail/search", { mailbox: ensureMailbox(mailbox), q, top }),
        );
        return items.map((m) => mapItem(m));
    },

    /**
     * NACHTRAG 2026-07-05 — Archiv = der ECHTE Graph-Archiv-Ordner (Well-Known
     * `archive`), nicht mehr „gelesene Inbox". Dorthin kommt eine Mail nur über
     * den Archivieren-Button oder automatisch nach Vorgang-Erstellung.
     */
    async fetchArchive(mailbox: string, opts?: ListenOptionen): Promise<Email[]> {
        return alsListe(await cached(
            `mail.inbox.archive:${mailbox}`,
            async () => {
                const items = await fetchJson<BackendMailListItem[]>(
                    apiUrl("mail/inbox", { mailbox: ensureMailbox(mailbox), folder: "archive", top: 100 }),
                );
                return items.map((m) => mapItem(m));
            },
            { ttlMs: MAIL_TTL_MS },
        ), opts);
    },

    /** T-229 — die Einladung hinter der Mail; `einladung: null` = keine. */
    async fetchEinladung(
        mailbox: string,
        messageId: string,
    ): Promise<{ einladung: TerminEinladung | null; darfAntworten: boolean }> {
        return fetchJson(apiUrl(`mail/messages/${encodeURIComponent(messageId)}/einladung`, { mailbox: ensureMailbox(mailbox) }));
    },

    /** T-229 — Einladung beantworten; Graph schickt die Antwort an den Organisator. */
    async beantworteEinladung(
        mailbox: string,
        messageId: string,
        antwort: EinladungsAntwort,
        kommentar?: string,
        /** `false` = „E-Mail an Organisator" aus: nur den Kalender setzen. */
        antwortmail?: boolean,
    ): Promise<{ einladung: TerminEinladung }> {
        const res = await fetchJson<{ einladung: TerminEinladung }>(
            apiUrl(`mail/messages/${encodeURIComponent(messageId)}/einladung`),
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    mailbox: ensureMailbox(mailbox),
                    antwort,
                    kommentar: kommentar ?? "",
                    ...(antwortmail === false ? { antwortmail: false } : {}),
                }),
            },
        );
        // Der Kalender zeigt den neuen Status erst nach dem nächsten Abruf.
        graphMailService.invalidateMailbox(mailbox);
        return res;
    },

    /** SP3 — manueller Sofort-Sync: Server-Delta anstoßen + lokale Caches verwerfen. */
    async syncMailbox(mailbox: string): Promise<void> {
        await fetchJson<{ ok: boolean }>(
            apiUrl("mail/sync", { mailbox: ensureMailbox(mailbox) }),
            { method: "POST" },
        );
        graphMailService.invalidateMailbox(mailbox);
    },

    /**
     * Mail ins echte Archiv verschieben (Graph-Move) + Listen-Caches verwerfen.
     * SP9 — Archiv-Semantik: NUR der „Archivieren"-Button und das Auto-Archiv
     * nach Vorgang/Todo-Erstellung bzw. -Zuweisung (`MailDetailWindow.finishRead`)
     * rufen das hier auf; bloßes Lesen/Schließen markiert höchstens `isRead`.
     * Kanonische Doku: apps/api/src/mail/graph-mail.service.ts → archiveMessage.
     */
    /**
     * Schadenpostfach: die Mail samt allen Anhängen in einen Ordner der
     * Kundenakte des Kontakts legen (`ordner` relativ zur Akte, z. B.
     * „Schaden/2026 Wasserschaden"). Archiviert NICHT — das stößt der Aufrufer
     * danach über `archiveMessage` an.
     */
    async schadenAblage(input: {
        mailbox: string;
        messageId: string;
        contactId: number;
        ordner: string;
    }): Promise<{ status: "created" | "duplicate"; ordner: string; documentId: number; anhaenge: number; hinweis: string | null }> {
        return fetchJson(apiUrl("mail/schaden-ablage"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ...input, mailbox: ensureMailbox(input.mailbox) }),
        });
    },

    async archiveMessage(
        mailbox: string,
        messageId: string,
        opts: {
            contactId?: number | null;
            vorgangId?: number | null;
            /** T-240: einmal, wenn auf die laufende Mail-Sicherung gewartet wird. */
            onWarten?: () => void;
        } = {},
    ): Promise<{ ok: boolean; id: string | null; archive: MailArchiveStatusInfo | null }> {
        // Cluster ABLAGE (M1/E09): Der Server sichert die Mail zusätzlich
        // dauerhaft als .eml. Ein bereits erkannter Kontakt reist mit — das
        // spart der Ablage die Adressauflösung und trifft den Kontakt, den der
        // Nutzer tatsächlich sieht. Die Antwort sagt, ob es geklappt hat: bei
        // `skipped` (über 35 MB, E11) wurde NICHTS gesichert, und genau das
        // muss dem Nutzer gesagt werden.
        //
        // T-240: Eine verknüpfte Mail verschiebt der Server erst nach ihrer
        // Sicherung (409 MAIL_CAPTURE_PENDING) — hier wird kurz gewartet und
        // wiederholt, für ALLE Aufrufer (Liste, Detailfenster, Sammelaktion).
        // Dieselbe Id ist richtig: der Server heilt eine veraltete selbst.
        // Bricht nur der Browser ab, fragt `archivierenTrotzZeitlimit` einmal
        // nach — der Server hat die Mail womöglich längst verschoben.
        const versuch = () =>
            mitSicherungsWarten(
                () =>
                    fetchJson<{
                        ok: boolean;
                        id: string | null;
                        archive: MailArchiveStatusInfo | null;
                    }>(
                        apiUrl(`mail/messages/${encodeURIComponent(messageId)}/archive`, { mailbox: ensureMailbox(mailbox) }),
                        {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                                contactId: opts.contactId ?? undefined,
                                vorgangId: opts.vorgangId ?? undefined,
                            }),
                        },
                        { timeoutMs: ARCHIVE_TIMEOUT_MS },
                    ),
                { onWarten: opts.onWarten },
            );
        const res = await archivierenTrotzZeitlimit(versuch, { ok: true, id: null, archive: null }, isGraph404);
        graphMailService.invalidateMailbox(mailbox);
        return res;
    },

    /**
     * Nachricht in einen beliebigen Ordner DESSELBEN Postfachs verschieben
     * (Graph-Move). `destinationId` = Well-Known-Name (`inbox`, `archive`,
     * `deleteditems`, `drafts`, `sentitems`, `junkemail`) ODER opake Graph-Ordner-Id
     * aus `fetchFolderTree`. „In Posteingang verschieben" nutzt `destinationId:
     * 'inbox'`. Graph vergibt beim Move eine NEUE Message-Id (im Ergebnis); die alte
     * Id ist danach tot → Aufrufer verwerfen die Zeile. Verwirft die Listen-/
     * Ordner-Caches des Postfachs. Postfachübergreifend siehe
     * `moveMessageToMailbox` — der Graph-Move selbst ist ordnerlokal.
     */
    async moveMessage(
        mailbox: string,
        messageId: string,
        destinationId: string,
    ): Promise<{ ok: boolean; id: string | null; archive: MailArchiveStatusInfo | null }> {
        // Cluster ABLAGE: Verschieben sichert dauerhaft — außer nach Papierkorb,
        // Junk und Entwürfe. `archive` sagt, ob es geklappt hat (additiv).
        const res = await fetchJson<{
            ok: boolean;
            id: string | null;
            archive: MailArchiveStatusInfo | null;
        }>(
            apiUrl(`mail/messages/${encodeURIComponent(messageId)}/move`),
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ mailbox: ensureMailbox(mailbox), destinationId }),
            },
        );
        graphMailService.invalidateMailbox(mailbox);
        return res;
    },

    /**
     * Cluster POSTFACH (E02-Zusatz) — Nachricht in einen Ordner eines ANDEREN
     * Postfachs ablegen (persönliches Postfach ↔ CBB).
     *
     * Serverseitig ist das KEIN Graph-Move (den gibt es über Postfachgrenzen
     * nicht), sondern MIME-Kopie ins Ziel plus Papierkorb in der Quelle. Folgen,
     * die der Aufrufer kennen muss: die Kopie hat eine NEUE Message-Id und in
     * aller Regel eine neue conversationId; Kategorien und Kennzeichen gehen
     * verloren; und `sourceRemoved === false` heißt, dass die Kopie zwar steht,
     * das Original aber noch im Quell-Postfach liegt — das gehört dem Nutzer
     * gesagt, statt es als Erfolg zu verbuchen.
     */
    async moveMessageToMailbox(
        sourceMailbox: string,
        messageId: string,
        targetMailbox: string,
        destinationId: string,
    ): Promise<{
        ok: boolean;
        mailbox: string;
        id: string;
        conversationId: string | null;
        sourceRemoved: boolean;
        relinked: number;
    }> {
        const res = await fetchJson<{
            ok: boolean;
            mailbox: string;
            id: string;
            conversationId: string | null;
            sourceRemoved: boolean;
            relinked: number;
        }>(apiUrl(`mail/messages/${encodeURIComponent(messageId)}/move-to-mailbox`), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                mailbox: ensureMailbox(sourceMailbox),
                targetMailbox: ensureMailbox(targetMailbox),
                destinationId,
            }),
        });
        // BEIDE Postfächer haben sich geändert — die Quelle verliert die Mail,
        // das Ziel bekommt sie.
        graphMailService.invalidateMailbox(sourceMailbox);
        graphMailService.invalidateMailbox(targetMailbox);
        return res;
    },

    async fetchFolders(
        folderName: "drafts" | "sentitems",
        mailbox: string,
        opts?: ListenOptionen,
    ): Promise<Email[]> {
        return alsListe(await cached(
            `mail.folder.${folderName}:${mailbox}`,
            async () => {
                const items = await fetchJson<BackendMailListItem[]>(
                    apiUrl("mail/inbox", { mailbox: ensureMailbox(mailbox), folder: folderName, top: 100 }),
                );
                return items.map((m) => mapItem(m));
            },
            { ttlMs: MAIL_TTL_MS },
        ), opts);
    },

    /**
     * T1: alle gecachten Mail-Daten EINES Postfachs verwerfen (Ordnerbaum +
     * Listen aller Ordner), damit ein anschließendes Refetch frische Zähler/
     * Nachrichten holt. Wird vom Echtzeit-`mail`-Frame (PR5) aufgerufen.
     */
    invalidateMailbox(mailbox: string): void {
        invalidate(`mail.folders:${mailbox}`);
        invalidate(`mail.inbox.paged:${mailbox}`);
        // T-179: die ersten Seiten der übrigen Ordner (`fetchInboxPaged` mit Ordner).
        invalidateByPrefix(`mail.inbox.paged:${mailbox}:`);
        invalidate(`mail.inbox.archive:${mailbox}`);
        invalidate(`mail.folder.drafts:${mailbox}`);
        invalidate(`mail.folder.sentitems:${mailbox}`);
        invalidate(`mail.inbox.matched:${mailbox}`);
        invalidateByPrefix(`mail.folder.id:${mailbox}:`);
        invalidateByPrefix(`mail.unread.folder:${mailbox}:`);
        // SP4: Detail-Bodies (`fetchMessageBody`) und Konversationen
        // (`fetchConversation`) desselben Postfachs mitverwerfen — so decken
        // Frame-Handler, syncMailbox, archive/move und der Reconnect-Pfad die
        // Detail-Keys automatisch ab, ohne an jeder Aufrufstelle einzeln zu
        // invalidieren.
        invalidateByPrefix(`mail.msg:${mailbox}:`);
        invalidateByPrefix(`mail.conv:${mailbox}:`);
        // Eine gesendete Antwort verändert den Thread-Index — sonst bliebe der
        // Pfeil an der Zeile bis zum Ablauf der TTL auf dem alten Stand.
        invalidate(`mail.threadcounts:${mailbox}`);
        invalidate(`mail.draftindex:${mailbox}`);
    },

    /**
     * T3: vollständiger Ordnerbaum eines Postfachs (GET /mail/folders). Setzt
     * voraus, dass der Backend-Endpoint deployt ist (PR1); sonst wirft fetchJson.
     */
    async fetchFolderTree(mailbox: string): Promise<MailFolder[]> {
        return cached(
            `mail.folders:${mailbox}`,
            () => fetchJson<MailFolder[]>(apiUrl("mail/folders", { mailbox: ensureMailbox(mailbox) })),
            { ttlMs: FOLDER_TTL_MS },
        );
    },

    /**
     * T3: Nachrichten eines BELIEBIGEN Ordners (per Graph-Folder-Id). Der
     * Backend-`/mail/inbox` akzeptiert seit PR1 echte Folder-Ids als `folder`.
     */
    async fetchMessagesByFolder(mailbox: string, folderId: string, opts?: ListenOptionen): Promise<Email[]> {
        return alsListe(await cached(
            `mail.folder.id:${mailbox}:${folderId}`,
            async () => {
                const items = await fetchJson<BackendMailListItem[]>(
                    apiUrl("mail/inbox", { mailbox: ensureMailbox(mailbox), folder: folderId, top: 100 }),
                );
                return items.map((m) => mapItem(m));
            },
            { ttlMs: MAIL_TTL_MS },
        ), opts);
    },

    /**
     * Vg-SP5: eine SEITE der Nachrichten eines beliebigen Ordners, UNGRUPPIERT
     * (eine `messageId` je Zeile) — für den Vorgang-Mail-Picker, wo Checkbox =
     * eine Mail gilt (im Gegensatz zu `fetchMessagesByFolder`, das Threads via
     * groupConversations zusammenfasst). Gegen den vorhandenen `GET /mail/inbox`,
     * der `folder` + `skip` + `top` seit Mail-SP2 unterstützt → „Mehr laden"
     * blättert über `skip`. BEWUSST UNgecacht: ad-hoc-Paginierung; der Picker
     * schließt beim Bestätigen ohnehin und braucht keinen Zwischencache.
     */
    async fetchFolderMessagesPage(
        mailbox: string,
        folderId: string,
        skip: number,
        top: number,
    ): Promise<Email[]> {
        const items = await fetchJson<BackendMailListItem[]>(
            apiUrl("mail/inbox", { mailbox: ensureMailbox(mailbox), folder: folderId, top, skip }),
        );
        return reviveEmailDates(items.map((m) => mapItem(m)));
    },

    /**
     * SP4 — Voll-Body einer Mail (der EINZIGE zuvor ungecachte Mail-Read). Kurz
     * gecacht (5 min), damit Zurück-Springen zwischen Mails / erneutes Öffnen
     * ohne Round-Trip rendert. WICHTIG: `persist:false` — der Body enthält nach
     * dem cid:→data:-Rewrite Base64-Inline-Bilder; die dürfen NIE nach
     * localStorage (Quota). Memory-only behält zudem das echte `date: Date`
     * (kein reviveEmailDates nötig). Invalidiert über `invalidateMailbox`
     * (Prefix `mail.msg:{mailbox}:`) → Frame-Handler/Sync/Archiv/Move/Reconnect
     * verwerfen den Detail-Cache automatisch mit.
     */
    async fetchMessageBody(mailbox: string, messageId: string): Promise<Email> {
        return cached(
            `mail.msg:${mailbox}:${messageId}`,
            async () => {
                // Mail-SP7: `progressive=1` → große/bilderreiche Mails liefern den
                // Body SOFORT mit unersetzten `cid:` + `pendingInlineCids`; die
                // Bilder lädt die Komponente danach (`patchInlineImages`) nach.
                // Der Server gatet zusätzlich (kleine Mails kommen synchron mit
                // `data:`-URIs). Kill-Switch-fähig → sonst der alte Ein-Call-Pfad.
                const params: Record<string, string | number | boolean> = {
                    mailbox: ensureMailbox(mailbox),
                };
                if (isMailProgressiveInlineEnabled()) params.progressive = 1;
                const detail = await fetchJson<BackendMailDetail>(
                    apiUrl(`mail/messages/${messageId}`, params),
                );
                return mapItem(detail, detail);
            },
            { ttlMs: 5 * 60_000, persist: false },
        );
    },

    /**
     * Mail-SP7 — progressives Nachladen der Inline-Bilder EINER Mail. Für jede
     * `email.pendingInlineCids` wird `mail/messages/:id/inline/:cid` geholt und
     * `cid:`→`data:` im Body gepatcht; Fehler je Bild sind graceful (bleibt leer).
     * Die gepatchte Fassung wird in den Detail-Cache (`mail.msg:…`, memory-only)
     * zurückgelegt, sodass erneutes Öffnen ohne Neu-Fetch rendert.
     *
     * `requestId` = die Id, mit der `fetchMessageBody` gecacht hat (Cache-Key);
     * die Graph-Calls nutzen `email.id` (die vom Body zurückgelieferte, ggf.
     * self-geheilte aktuelle Id) — wichtig für Beteiligte/Freigabe-Zugriff.
     * Rückgabe: die (immer) mit geleerten `pendingInlineCids` versehene Fassung —
     * es wird NICHT automatisch erneut versucht (kein Loop bei Teil-Fehlern).
     */
    async patchInlineImages(mailbox: string, requestId: string, email: Email): Promise<Email> {
        const cids = email.pendingInlineCids ?? [];
        if (cids.length === 0) return email;
        const box = ensureMailbox(mailbox);
        const entries = await Promise.all(
            cids.map(async (cid) => {
                try {
                    const r = await fetchJson<{ contentId: string; contentType: string; contentBytes: string }>(
                        apiUrl(
                            `mail/messages/${encodeURIComponent(email.id)}/inline/${encodeURIComponent(cid)}`,
                            { mailbox: box },
                        ),
                    );
                    return { cid, dataUri: `data:${r.contentType};base64,${r.contentBytes}` };
                } catch (error) {
                    reportMailError({
                        area: "mail",
                        action: "load-inline-images",
                        error,
                        fallbackMessage: "Ein eingebettetes Bild konnte nicht geladen werden.",
                    });
                    return null;
                }
            }),
        );
        const byCid = new Map<string, string>();
        for (const e of entries) if (e) byCid.set(cidSchluessel(e.cid), e.dataUri);
        // Nach dem Versuch IMMER pendingInlineCids leeren (kein Auto-Retry-Loop);
        // fehlgeschlagene Bilder bleiben als `cid:` (leer) stehen.
        const patched: Email = {
            ...email,
            body: replaceCidWithDataUri(email.body, byCid),
            pendingInlineCids: [],
        };
        // Detail-Cache (memory-only, persist:false wie fetchMessageBody) überschreiben.
        poke(`mail.msg:${box}:${requestId}`, patched, false);
        return patched;
    },

    /** T2: Datei-Anhänge einer Mail listen (Metadaten). RBAC läuft serverseitig.
     *  Stempelt `messageId` auf jeden Anhang, damit der Download bei Thread-
     *  Anhängen die richtige Quell-Nachricht adressiert. */
    async fetchAttachments(mailbox: string, messageId: string): Promise<MailAttachment[]> {
        const list = await fetchJson<MailAttachment[]>(
            apiUrl(`mail/messages/${messageId}/attachments`, { mailbox: ensureMailbox(mailbox) }),
        );
        return list.map((a) => ({ ...a, messageId }));
    },

    /**
     * T2: einen Anhang herunterladen. Blob über den (bearer-gepatchten) fetch holen
     * — ein direkter `<a href>` würde 401 liefern (kein Authorization-Header).
     */
    /**
     * Anhang als Blob holen — Basis für Download UND Vorschau im KMVS-Viewer
     * (PO-Notiz 26.07.2026, Punkt 12).
     */
    async fetchAttachmentBlob(
        mailbox: string,
        messageId: string,
        attachmentId: string,
    ): Promise<Blob> {
        const res = await fetch(
            apiUrl(`mail/messages/${messageId}/attachments/${encodeURIComponent(attachmentId)}`, {
                mailbox: ensureMailbox(mailbox),
            }),
        );
        if (!res.ok) throw new Error(`Anhang-Download fehlgeschlagen (${res.status})`);
        return res.blob();
    },

    /** Die ganze Mail als `.eml` (rohes MIME, RBAC wie beim Lesen). */
    async fetchEmlBlob(mailbox: string, messageId: string): Promise<Blob> {
        const res = await fetch(
            apiUrl(`mail/messages/${messageId}/eml`, { mailbox: ensureMailbox(mailbox) }),
        );
        if (!res.ok) throw new Error(`Mail-Download fehlgeschlagen (${res.status})`);
        return res.blob();
    },

    async downloadAttachment(
        mailbox: string,
        messageId: string,
        attachmentId: string,
        fileName: string,
    ): Promise<void> {
        const blob = await this.fetchAttachmentBlob(mailbox, messageId, attachmentId);
        const url = URL.createObjectURL(blob);
        try {
            const a = document.createElement("a");
            a.href = url;
            a.download = fileName || "anhang";
            document.body.appendChild(a);
            a.click();
            a.remove();
        } finally {
            URL.revokeObjectURL(url);
        }
    },

    /**
     * Ausgewählte Mail-Anhänge als Kundendokumente übernehmen (konsolidierter
     * Endpoint `POST /mail/attachments/import`). EIN Aufruf sowohl fürs
     * Lesepanel („Beim Kontakt speichern", ein Anhang, ohne `vorgangId`) als
     * auch für den Vorgang-Erstellflow (mehrere Anhänge, mit `vorgangId`). Die
     * Antwort meldet je Anhang created/duplicate/error (idempotent).
     *
     * `contactId` ist seit 14.09.2026 optional: entsteht aus der Mail ein
     * PERSÖNLICHER Vorgang, gibt es keinen Kunden — dann ist `vorgangId` das
     * Ablageziel (und Pflicht), und der Server prüft den Vorgang statt des
     * Kontakts.
     */
    async importAttachments(params: {
        mailbox: string;
        contactId?: number | null;
        vorgangId?: number | null;
        /** T-18: To-do, an dem eine schon vorhandene Datei im Vorgang verknüpft wird. */
        todoId?: number | null;
        attachments: MailAttachmentImportItem[];
        /** Drag & Drop in die Alte Kundenakte: Kategorie und offener Ordner. */
        kategorie?: string | null;
        ordner?: string | null;
    }): Promise<MailAttachmentImportSummary> {
        return fetchJson<MailAttachmentImportSummary>(apiUrl("mail/attachments/import"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                mailbox: ensureMailbox(params.mailbox),
                contactId: params.contactId ?? undefined,
                vorgangId: params.vorgangId ?? undefined,
                todoId: params.todoId ?? undefined,
                kategorie: params.kategorie || undefined,
                ordner: params.ordner || undefined,
                attachments: params.attachments,
            }),
        });
    },

    async markRead(messageId: string, mailbox: string): Promise<void> {
        await fetchJson(apiUrl(`mail/messages/${messageId}/read`, { mailbox: ensureMailbox(mailbox) }), {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ isRead: true }),
        });
        invalidateByPrefix("mail.inbox.");
        invalidateByPrefix("mail.folder.");
        // Der Ungelesen-Zaehler am Ordner ist Graphs `unreadItemCount` und steckt
        // im Ordnerbaum-Cache — ein eigener Key, den die beiden Praefixe oben
        // NICHT treffen. Ohne dieses Verwerfen liefert ein `refreshFolders`
        // direkt nach dem Umschalten den 5 Minuten alten Baum zurueck: der Punkt
        // an der Zeile springt um, die Zahl in der Ordnerliste bleibt stehen.
        invalidate(`mail.folders:${mailbox}`);
    },

    async markAsUnread(messageId: string, mailbox: string): Promise<void> {
        await fetchJson(apiUrl(`mail/messages/${messageId}/read`, { mailbox: ensureMailbox(mailbox) }), {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ isRead: false }),
        });
        invalidateByPrefix("mail.inbox.");
        invalidateByPrefix("mail.folder.");
        // Siehe `markRead`: der Ordnerbaum traegt die Ungelesen-Zahl und liegt
        // unter einem eigenen Cache-Key.
        invalidate(`mail.folders:${mailbox}`);
    },

    async sendMail(
        mailbox: string,
        to: string[],
        subject: string,
        body: string,
        cc: string[] = [],
        bcc: string[] = [],
        opts: {
            contentType?: "Text" | "HTML";
            replyToMessageId?: string;
            /** SP6 (C3): Inline-Bilder + Datei-Anhänge (Graph-Passthrough). */
            attachments?: SendMailAttachment[];
            /**
             * Vorgang, an den die gesendete Mail gehängt wird (PO 26.07.2026) —
             * löst den sichtbaren `[VG-<id>]`-Betreffmarker ab.
             */
            vorgangId?: number;
            /** T-259: To-do, an das die gesendete Mail gehängt wird (Vorrang vor vorgangId). */
            todoId?: number;
            /**
             * „Erbe den Vorgangsbezug dieser Nachricht": beim Antworten und
             * Weiterleiten die Id der Ursprungsnachricht. Das Backend löst
             * darüber die Konversation auf.
             */
            inheritLinksFromMessageId?: string;
            /**
             * Postfach, in dem `replyToMessageId` / `inheritLinksFromMessageId`
             * liegt (2026-07-29). Nötig, weil Graph-Message-Ids postfachgebunden
             * sind: Wer aus einem Vorgang heraus antwortet und als Absender sein
             * eigenes Postfach wählt, hätte sonst einen Abbruch — das Backend
             * suchte das Original im falschen Postfach.
             */
            replySourceMailbox?: string;
            /**
             * Zuweisen beim Antworten (User 02.11.2026): Kontakt, dem die
             * gesendete Mail zugewiesen wird.
             */
            kontaktId?: number;
        } & VersandKennung = {},
    ): Promise<VersandAntwort> {
        return fetchJson<VersandAntwort>(apiUrl("mail/send"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                mailbox: ensureMailbox(mailbox),
                to,
                cc,
                bcc,
                subject,
                body,
                contentType: opts.contentType ?? "HTML",
                replyToMessageId: opts.replyToMessageId,
                attachments: opts.attachments,
                vorgangId: opts.vorgangId,
                todoId: opts.todoId,
                inheritLinksFromMessageId: opts.inheritLinksFromMessageId,
                replySourceMailbox: opts.replySourceMailbox
                    ? ensureMailbox(opts.replySourceMailbox)
                    : undefined,
                kontaktId: opts.kontaktId,
                sendeId: opts.sendeId,
                wiederholung: opts.wiederholung || undefined,
            }),
        }, { timeoutMs: SEND_TIMEOUT_MS });
    },

    /**
     * T-340 — Ausgang eines Versands mit Kennung. Sendet nie; mit `mailbox`
     * sieht der Server bei unbekanntem Lauf in „Gesendete Elemente“ nach.
     */
    async holeVersandStatus(sendeId: string, mailbox?: string): Promise<VersandAntwort> {
        return fetchJson<VersandAntwort>(
            apiUrl(
                `mail/send-status/${encodeURIComponent(sendeId)}`,
                mailbox ? { mailbox: ensureMailbox(mailbox) } : undefined,
            ),
        );
    },

    /**
     * T-212 — Entwurf speichern: ohne `draftId` neu anlegen, sonst ändern.
     * Danach Ordner-Caches verwerfen — der Delta-Poll meldet Entwürfe nicht.
     */
    async saveDraft(payload: EntwurfPayload, draftId?: string | null): Promise<EntwurfStand> {
        const mailbox = ensureMailbox(payload.mailbox);
        const body = JSON.stringify(entwurfBody({ ...payload, mailbox }));
        try {
            return await fetchJson<EntwurfStand>(
                apiUrl(draftId ? `mail/drafts/${encodeURIComponent(draftId)}` : "mail/drafts"),
                { method: draftId ? "PATCH" : "POST", headers: { "Content-Type": "application/json" }, body },
                { timeoutMs: SEND_TIMEOUT_MS },
            );
        } finally {
            graphMailService.invalidateMailbox(mailbox);
            meldeEntwurfGeaendert(mailbox);
        }
    },

    /**
     * T-212 (Teil B) — Entwurfs-Index: `conversationId → jüngster Antwort-
     * Entwurf`. Nachrangig wie der Thread-Index; Fehler → leere Karte.
     */
    async fetchDraftIndex(mailbox: string): Promise<EntwurfIndex> {
        try {
            return await cached(
                `mail.draftindex:${mailbox}`,
                () => fetchJson<EntwurfIndex>(apiUrl("mail/draft-index", { mailbox: ensureMailbox(mailbox) })),
                { ttlMs: MAIL_TTL_MS },
            );
        } catch {
            return {};
        }
    },

    /**
     * T-231 — Entwürfe, die aus diesem Vorgang heraus begonnen wurden (alle
     * Sende-Postfächer des Nutzers). Nachrangig: Fehler → leere Liste.
     */
    async fetchVorgangEntwuerfe(vorgangId: number): Promise<VorgangEntwurf[]> {
        try {
            return await fetchJson<VorgangEntwurf[]>(apiUrl("mail/vorgang-entwuerfe", { vorgangId: String(vorgangId) }));
        } catch {
            return [];
        }
    },

    /** T-212 — Entwurf zum Weiterschreiben laden. */
    async getDraft(mailbox: string, draftId: string): Promise<EntwurfDetail> {
        const mb = ensureMailbox(mailbox);
        return fetchJson<EntwurfDetail>(
            apiUrl(`mail/drafts/${encodeURIComponent(draftId)}?mailbox=${encodeURIComponent(mb)}`),
        );
    },

    /** T-212 — Entwurf mit dem aktuellen Inhalt senden. */
    async sendDraft(
        draftId: string,
        payload: EntwurfPayload,
        kennung: VersandKennung = {},
        /** Zuweisen beim Antworten (User 02.11.2026) — nur beim Senden, nie beim Speichern. */
        zuweisung: { kontaktId?: number } = {},
    ): Promise<VersandAntwort> {
        const mailbox = ensureMailbox(payload.mailbox);
        try {
            return await fetchJson<VersandAntwort>(
                apiUrl(`mail/drafts/${encodeURIComponent(draftId)}/send`),
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        ...entwurfBody({ ...payload, mailbox }),
                        contentType: "HTML",
                        kontaktId: zuweisung.kontaktId,
                        sendeId: kennung.sendeId,
                        wiederholung: kennung.wiederholung || undefined,
                    }),
                },
                { timeoutMs: SEND_TIMEOUT_MS },
            );
        } finally {
            graphMailService.invalidateMailbox(mailbox);
            meldeEntwurfGeaendert(mailbox);
        }
    },

    /** T-212 — Entwurf löschen (landet in „Gelöschte Elemente"). */
    async deleteDraft(mailbox: string, draftId: string): Promise<void> {
        const mb = ensureMailbox(mailbox);
        try {
            await fetchJson<{ ok: true }>(
                apiUrl(`mail/drafts/${encodeURIComponent(draftId)}?mailbox=${encodeURIComponent(mb)}`),
                { method: "DELETE" },
            );
        } finally {
            graphMailService.invalidateMailbox(mb);
            meldeEntwurfGeaendert(mb);
        }
    },

    /**
     * Backend already filters by contact visibility, so the dedicated "by sender"
     * call collapses to "fetch inbox; the backend annotates matchedContact and
     * removes hidden rows." We keep the helper for API parity, but the addresses
     * arg is ignored — visibility is now decided server-side.
     */
    async fetchBySenderAddresses(mailbox: string, _addresses: string[]): Promise<Email[]> {
        return reviveEmailDates(await cached(
            `mail.inbox.matched:${mailbox}`,
            async () => {
                const items = await fetchJson<BackendMailListItem[]>(
                    apiUrl("mail/inbox", { mailbox: ensureMailbox(mailbox), folder: "inbox", top: 200 }),
                );
                const matched = items.filter((m) => m.matchedContact);
                return groupConversations(matched.map((m) => mapItem(m)));
            },
            { ttlMs: MAIL_TTL_MS },
        ));
    },

    /**
     * Empfänger-Kontaktsuche für den Compose-Picker (SP5/C2). Fragt den schlanken,
     * mandanten-/RBAC-gescopten Endpoint `GET /contact/email-recipients` ab. Bei
     * leerem/zu kurzem `search` (< 2) oder jedem Fehler (z.B. 403) → defensiv `[]`,
     * damit der Picker still degradiert und die freie Eingabe nutzbar bleibt.
     */
    /**
     * T-22: Mit `kontextContactId` (Kunde des Vorgangs) liefert das Backend
     * zusätzlich die Vorschläge zu diesem Kontakt — vorn und auch ohne
     * Suchtext. Ohne Kontext bleibt es bei der reinen Tippsuche ab 2 Zeichen.
     */
    async searchEmailRecipients(
        search: string,
        opts?: { kontextContactId?: number | null; kontextContactIds?: readonly number[] | null },
    ): Promise<EmailRecipientHit[]> {
        const q = (search ?? "").trim();
        const kontext = opts?.kontextContactId ?? null;
        // T-256: weitere Kontakte der Kontextgruppe (z. B. alle Vorgangskontakte).
        const weitere = (opts?.kontextContactIds ?? []).filter((id) => id !== kontext);
        if (q.length < 2 && kontext == null && weitere.length === 0) return [];
        try {
            return await fetchJson<EmailRecipientHit[]>(
                apiUrl("contact/email-recipients", {
                    search: q,
                    ...(kontext != null ? { kontextContactId: kontext } : {}),
                    ...(weitere.length > 0 ? { kontextContactIds: weitere.join(",") } : {}),
                }),
            );
        } catch {
            return [];
        }
    },

    /**
     * SP6 (Mail-Kontakt-Sidebar, Reiter „Schriftwechsel"): E-Mail-Adressen eines
     * Kontakts — Basis für die postfachübergreifende `participants:`-Graph-Suche.
     * Sichtbarkeit prüft das Backend (canUserAccessContact); jeder Fehler
     * (z.B. 403) → defensiv `[]`, damit die Sidebar auf die Absenderadresse der
     * geöffneten Mail zurückfallen kann.
     */
    async fetchContactMailAddresses(
        contactId: number,
        opts?: { beziehungen?: boolean },
    ): Promise<string[]> {
        try {
            // K8: `beziehungen=1` zieht Firma ↔ Ansprechpartner in BEIDE Richtungen
            // mit. Bewusst opt-in — der Schriftwechsel-Reiter bleibt auf den
            // eigenen Adressen, nur der Termine-Block fragt die erweiterte Menge.
            const suffix = opts?.beziehungen ? '?beziehungen=1' : '';
            return await fetchJson<string[]>(
                apiUrl(`contact/${contactId}/mail-addresses${suffix}`),
            );
        } catch {
            return [];
        }
    },

    /** Fachlich enge Teilnehmer-Vorauswahl für einen neuen Termin. */
    async fetchContactTerminAttendeeAddresses(contactId: number): Promise<string[]> {
        return fetchJson<string[]>(
            apiUrl(`contact/${contactId}/termin-attendee-addresses`),
        );
    },

    /**
     * Cluster ERKENNUNG — die Erkennung einer Mail, EINMAL je geöffneter Mail.
     *
     * Der Rückgabewert ist ausdrücklich ein ZUSTAND und kein Ersatzwert. Vorher
     * schluckte ein `catch` jeden 4xx/5xx und gab `{contracts:[],risks:[]}`
     * zurück — für die Command-Leiste war das nicht von „nichts erkannt" zu
     * unterscheiden, und ein Fehler hätte sie dauerhaft stummgeschaltet.
     */
    async recognizeMail(mailbox: string, messageId: string): Promise<MailRecognitionOutcome> {
        try {
            const data = await fetchJson<MailRecognitionResponse>(
                apiUrl("mail/recognize", { mailbox: ensureMailbox(mailbox), messageId }),
            );
            return { status: "ok", data };
        } catch {
            return { status: "error" };
        }
    },

    /**
     * Cluster ERKENNUNG (A6) — der schlanke Zweit-Endpoint für den Chip-Wechsel.
     *
     * Fehler → `null`: Der Hinweis „diese Nummer fehlt noch" ist ein Extra, kein
     * Betriebsmittel; ein Fehlschlag darf die Leiste nicht behelligen.
     */
    async parseSignatureText(text: string): Promise<MailParsedSignature | null> {
        try {
            return await fetchJson<MailParsedSignature>(apiUrl("mail/recognize/signature-text"), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ text: text.slice(0, 4000) }),
            });
        } catch {
            return null;
        }
    },

    /**
     * Cluster ERKENNUNG (A6) — siehe oben; ein Fehler liefert `null`.
     */
    async fetchMissingContactData(
        mailbox: string,
        messageId: string,
        contactId: number,
    ): Promise<MailMissingContactData | null> {
        try {
            return await fetchJson<MailMissingContactData | null>(
                apiUrl("mail/recognize/missing-contact-data", {
                    mailbox: ensureMailbox(mailbox),
                    messageId,
                    contactId: String(contactId),
                }),
            );
        } catch {
            return null;
        }
    },
};
